import React from 'react'
import PropTypes from 'prop-types'
import { PortableText } from '@portabletext/react'
import Text from './Text'
import Spacer from './Spacer'
import EmbedHTML from './EmbedHTML'
import Figure from './Figure'
import Youtube from './Youtube'
import ImageEmbed from './Utils/ImageEmbed'
import BlogPostHighlightedText from './sections/Blog/BlogPostHighlightedText'
import ProductCard from './ProductCard'
import Img from './Img'
import { normalizeInternalHref } from './../utils/regions'
import { useUrlRegion } from './../utils/useCurrentRegion'

const defaultTextType = 'regular'

// Link mark renderer: region-scopes internal hrefs (commerce -> /<region>/…,
// content -> bare) and leaves external links untouched. Only external links
// open in a new tab. `region` is the PAGE's region from useUrlRegion() (null on
// content pages -> commerce links stay bare for the geo-IP redirect), closed
// over so scoping is SSR === first-client-render safe.
function renderLink(region) {
  const LinkMark = ({ value, children }) => {
    const href = normalizeInternalHref(value && value.href, region)
    // Internal hrefs are root-relative ('/…') after normalization; anything
    // else (mailto/tel/#anchor/external host) stays as authored and opens in a
    // new tab.
    const isExternal = typeof href === 'string' && !href.startsWith('/')
    const targetProps = isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {}
    return <a href={href} {...targetProps}>{children}</a>
  }
  LinkMark.displayName = 'SimpleBlockContentLink'
  return LinkMark
}

function SimpleBlockContent(props) {
  const { blocks, noSpace, applySpace, highlight, highlightSx, highlightExactMatch, bodyTextType } = props
  // Optional: force all BODY text (list items + non-heading paragraphs) to a
  // single Text type, leaving section headings at their authored size. Used by
  // the GT-2 cockpit specs to render at the compact Key Features size. Defaults
  // to the authored/`defaultTextType` behavior when not provided.
  const bodyType = bodyTextType || defaultTextType
  const region = useUrlRegion()

  if (!blocks) {
    console.error('Missing blocks')
    return null
  }

  return (
    <PortableText
      value={blocks}
      components={{
        types: {
          embedHTML: EmbedHTML,
          figure: Figure,
          customImage: ({ value }) => <ImageEmbed {...value}/>,
          youtube: ({ value }) => <Youtube url={value.url} applySpace={applySpace}/>,
          blogPostHighlightedText: ({ value }) => <BlogPostHighlightedText {...value}/>,
          // In-article buy card (pure; image leaf injected so it matches the server twin).
          productCard: ({ value }) => <ProductCard value={value} ImgComponent={Img}/>
        },
        marks: {
          color: (props) => {
            return <span style={{ color: props.value.hex }}>{props.children}</span>
          },
          link: renderLink(region)
        },
        listItem: {
          bullet: ({ children }) => <li><Text type={bodyType} highlight={highlight} highlightSx={highlightSx} highlightExactMatch={highlightExactMatch}>{children}</Text></li>,
          number: ({ children }) => <li><Text type={bodyType} highlight={highlight} highlightSx={highlightSx} highlightExactMatch={highlightExactMatch}>{children}</Text></li>
        },
        block: {
          h1: ({ children }) => (
            <span>
              <Text type="h1" highlight={highlight} highlightSx={highlightSx} highlightExactMatch={highlightExactMatch}>{children}</Text>
              {!noSpace && <Spacer size={32}/>}
            </span>
          ),
          h2: ({ children }) => (
            <span>
              <Text type="h2" highlight={highlight} highlightSx={highlightSx} highlightExactMatch={highlightExactMatch}>{children}</Text>
              {!noSpace && <Spacer size={32}/>}
            </span>
          ),
          h3: ({ children }) => (
            <span>
              <Text type="h3" highlight={highlight} highlightSx={highlightSx} highlightExactMatch={highlightExactMatch}>{children}</Text>
              {!noSpace && <Spacer size={32}/>}
            </span>
          ),
          h4: ({ children }) => (
            <span>
              <Text type="h4" highlight={highlight} highlightSx={highlightSx} highlightExactMatch={highlightExactMatch}>{children}</Text>
              {!noSpace && <Spacer size={16}/>}
            </span>
          ),
          h5: ({ children }) => (
            <span>
              <Text type="h5" highlight={highlight} highlightSx={highlightSx} highlightExactMatch={highlightExactMatch}>{children}</Text>
              {!noSpace && <Spacer size={16}/>}
            </span>
          ),
          'body-short-1': (props) => {
              return <Text type="body-short-1" highlight={highlight} highlightSx={highlightSx} highlightExactMatch={highlightExactMatch}>{props.children}</Text>
          },
          'body-short-2': (props) => {
              return <Text type="body-short-2" highlight={highlight} highlightSx={highlightSx} highlightExactMatch={highlightExactMatch}>{props.children}</Text>
          },
          normal: (props) => {
              return <Text type={bodyType} highlight={highlight} highlightSx={highlightSx} highlightExactMatch={highlightExactMatch}>{props.children}</Text>
          },
          'body-long-1': (props) => {
              return <Text type="body-long-1" highlight={highlight} highlightSx={highlightSx} highlightExactMatch={highlightExactMatch}>{props.children}</Text>
          },
          'body-long-2': (props) => {
              return <Text type="body-long-2" highlight={highlight} highlightSx={highlightSx} highlightExactMatch={highlightExactMatch}>{props.children}</Text>
          },
          'caption-1': (props) => {
              return <Text type="caption-1" highlight={highlight} highlightSx={highlightSx} highlightExactMatch={highlightExactMatch}>{props.children}</Text>
          },
          small: (props) => {
            return <>
              {applySpace && <Spacer size={8}/>}
              <Text type={bodyTextType || 'small'} highlight={highlight}>{props.children}</Text>
              {applySpace && <Spacer size={4}/>}
            </>
          },
          regular: (props) => {
            return <>
              {applySpace && <Spacer size={8}/>}
              <Text type={bodyTextType || 'regular'} highlight={highlight}>{props.children}</Text>
              {applySpace && <Spacer size={4}/>}
            </>
          },
          medium: (props) => {
            return <>
              {applySpace && <Spacer size={8}/>}
              <Text type={bodyTextType || 'medium'} highlight={highlight} highlightSx={highlightSx} highlightExactMatch={highlightExactMatch}>{props.children}</Text>
              {applySpace && <Spacer size={4}/>}
            </>
          },
          large: (props) => {
            return <>
              {applySpace && <Spacer size={8}/>}
              <Text type={bodyTextType || 'large'} highlight={highlight} highlightSx={highlightSx} highlightExactMatch={highlightExactMatch}>{props.children}</Text>
              {applySpace && <Spacer size={4}/>}
            </>
          },
          'heading-1': (props) => {
            return <>
              {applySpace && <Spacer size={24}/>}
              <Text type="heading-1" highlight={highlight} highlightSx={highlightSx} highlightExactMatch={highlightExactMatch}>{props.children}</Text>
              {applySpace && <Spacer size={16}/>}
            </>

          },
          'heading-2': (props) => {
            return <>
              {applySpace && <Spacer size={24}/>}
              <Text type="heading-2" highlight={highlight} highlightSx={highlightSx} highlightExactMatch={highlightExactMatch}>{props.children}</Text>
              {applySpace && <Spacer size={16}/>}
            </>
          },
          'heading-3': (props) => {
            return <>
              {applySpace && <Spacer size={24}/>}
              <Text type="heading-3" highlight={highlight} highlightSx={highlightSx} highlightExactMatch={highlightExactMatch}>{props.children}</Text>
              {applySpace && <Spacer size={16}/>}
            </>
          },
          'heading-4': (props) => {
            return <>
              {applySpace && <Spacer size={24}/>}
              <Text type="heading-4" highlight={highlight} highlightSx={highlightSx} highlightExactMatch={highlightExactMatch}>{props.children}</Text>
              {applySpace && <Spacer size={16}/>}
            </>
          },
          'heading-5': (props) => {
            return <>
              {applySpace && <Spacer size={20}/>}
              <Text type="heading-5" highlight={highlight} highlightSx={highlightSx} highlightExactMatch={highlightExactMatch}>{props.children}</Text>
              {applySpace && <Spacer size={16}/>}
            </>
          },
          'heading-6': (props) => {
            return <>
              {applySpace && <Spacer size={20}/>}
              <Text type="heading-6" highlight={highlight} highlightSx={highlightSx} highlightExactMatch={highlightExactMatch}>{props.children}</Text>
              {applySpace && <Spacer size={16}/>}
            </>
          },
          'heading-7': (props) => {
            return <>
              {applySpace && <Spacer size={20}/>}
              <Text type="heading-7" highlight={highlight} highlightSx={highlightSx} highlightExactMatch={highlightExactMatch}>{props.children}</Text>
              {applySpace && <Spacer size={16}/>}
            </>
          },
          'heading-8': (props) => {
            return <>
              {applySpace && <Spacer size={8}/>}
              <Text type="heading-8" highlight={highlight} highlightSx={highlightSx} highlightExactMatch={highlightExactMatch}>{props.children}</Text>
              {applySpace && <Spacer size={4}/>}
            </>
          },
          'heading-9': (props) => {
            return <>
              {applySpace && <Spacer size={8}/>}
              <Text type="heading-9" highlight={highlight} highlightSx={highlightSx} highlightExactMatch={highlightExactMatch}>{props.children}</Text>
              {applySpace && <Spacer size={4}/>}
            </>
          },
          'heading-10': (props) => {
            return <>
              {applySpace && <Spacer size={4}/>}
              <Text type="heading-10" highlight={highlight} highlightSx={highlightSx} highlightExactMatch={highlightExactMatch}>{props.children}</Text>
              {applySpace && <Spacer size={4}/>}
            </>
          }
        }
      }}
    />
  )
}

SimpleBlockContent.propTypes = {
  blocks: PropTypes.arrayOf(PropTypes.object),
  bodyTextType: PropTypes.string,
}

export default SimpleBlockContent
